import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Group } from "../api/client";
import {
  BanDuration,
  Chat,
  ChatBan,
  ChatMember,
  ChatUserType,
  chatsApi,
  currentAdmin,
  LastMessage,
  NewChatMember,
} from "../api/chats";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./ChatsPage.css";

type ChatKind = "team" | "parents" | "custom";

type ChatRow = {
  chat: Chat;
  kind: ChatKind;
  group: Group | null;
  members: number | null;
  lastAt: string | null;
};

type PersonType = Exclude<ChatUserType, "admin">;

type Person = { key: string; id: string; type: PersonType; name: string; groupID: string | null };

const PARENTS_SUFFIX = " — родители";

const KIND_LABEL: Record<ChatKind, string> = {
  team: "Командный",
  parents: "Родительский",
  custom: "Отдельный",
};

const TYPE_LABEL: Record<ChatUserType, string> = {
  player: "Ребёнок",
  coach: "Тренер",
  parent: "Родитель",
  admin: "Администратор",
};

const PICKER_SECTIONS: { type: PersonType; title: string }[] = [
  { type: "player", title: "Дети" },
  { type: "coach", title: "Тренеры" },
  { type: "parent", title: "Родители" },
];

const DURATIONS: { value: BanDuration; label: string }[] = [
  { value: "forever", label: "Навсегда" },
  { value: "1d", label: "1 день" },
  { value: "1h", label: "1 час" },
  { value: "10m", label: "10 минут" },
];

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

// The groups list carries only chat_id; parent_chat_id is not in the API
// response. A parents chat is therefore recognised by what the server gives
// it: created by "system" and named "<group> — родители". It is a heuristic —
// a renamed parents chat shows up as a separate one.
function kindOf(chat: Chat, groups: Group[]): { kind: ChatKind; group: Group | null } {
  const team = groups.find((g) => g.chat_id === chat.id);
  if (team) return { kind: "team", group: team };
  if (chat.created_by === "system" && chat.name.endsWith(PARENTS_SUFFIX)) {
    const groupName = chat.name.slice(0, -PARENTS_SUFFIX.length);
    return { kind: "parents", group: groups.find((g) => g.name === groupName) ?? null };
  }
  return { kind: "custom", group: null };
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function syncNote(row: ChatRow): string {
  const group = row.group ? `группы «${row.group.name}»` : "группы";
  return row.kind === "team"
    ? `Состав этого чата сервер ведёт сам: дети и тренеры ${group}. Ручные изменения могут откатиться, когда в группе что-то поменяется — например, ребёнка переведут или тренера назначат.`
    : `Состав этого чата сервер ведёт сам: родители активных детей ${group}. Ручные изменения могут откатиться, когда у родителя или его детей что-то поменяется.`;
}

const toMembers = (people: Person[], picked: string[]): NewChatMember[] =>
  people
    .filter((p) => picked.includes(p.key))
    .map((p) => ({ user_id: p.id, user_type: p.type, user_name: p.name, role: "member" }));

function PeoplePicker({
  legend,
  people,
  picked,
  groupName,
  emptyText,
  disabled,
  onChange,
}: {
  legend: string;
  people: Person[];
  picked: string[];
  groupName: Map<string, string>;
  emptyText: string;
  disabled: boolean;
  onChange: (picked: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = q ? people.filter((p) => p.name.toLowerCase().includes(q)) : people;
  const toggle = (key: string) =>
    onChange(picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]);

  return (
    <fieldset className="chats-picker" disabled={disabled}>
      <legend>{legend}</legend>
      {people.length === 0 ? (
        <p className="mav-hint">{emptyText}</p>
      ) : (
        <>
          <label className="cp-field chats-picker-search">
            <span>Найти человека</span>
            <input className="mav-input" type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {visible.length === 0 ? (
            <p className="mav-hint">Никого не нашлось</p>
          ) : (
            <div className="chats-picker-sections">
              {PICKER_SECTIONS.map((section) => {
                const list = visible.filter((p) => p.type === section.type);
                if (list.length === 0) return null;
                return (
                  <div key={section.type} className="chats-picker-section" role="group" aria-label={section.title}>
                    <p className="chats-picker-title">{section.title}</p>
                    <ul className="chats-picker-list">
                      {list.map((p) => (
                        <li key={p.key}>
                          <label className="chats-picker-option">
                            <input type="checkbox" checked={picked.includes(p.key)} onChange={() => toggle(p.key)} />
                            <span>{p.name}</span>
                            {p.groupID && groupName.get(p.groupID) && (
                              <span className="chats-picker-note">{groupName.get(p.groupID)}</span>
                            )}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          {picked.length > 0 && <p className="mav-hint">Выбрано: {picked.length}</p>}
        </>
      )}
    </fieldset>
  );
}

export default function ChatsPage() {
  const [rows, setRows] = useState<ChatRow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);

  const [openID, setOpenID] = useState<string | null>(null);
  const [members, setMembers] = useState<ChatMember[] | null>(null);
  const [bans, setBans] = useState<ChatBan[]>([]);
  const [addPicked, setAddPicked] = useState<string[]>([]);
  const [banUser, setBanUser] = useState("");
  const [banDuration, setBanDuration] = useState<BanDuration>("forever");
  const [banReason, setBanReason] = useState("");
  const openRef = useRef<string | null>(null);
  const detailsRef = useRef<HTMLElement>(null);
  const { confirm, dialog } = useConfirm();

  const refreshChats = useCallback(async () => {
    try {
      const [chatList, groupList] = await Promise.all([chatsApi.listChats(), api.listGroups()]);
      const chats = chatList ?? [];
      const groupsNow = groupList ?? [];
      // Counts and last messages only fill columns; the list must still show without them.
      const [counts, last] = await Promise.all([
        Promise.all(
          chats.map((c) =>
            chatsApi.countMembers(c.id).catch((err) => {
              console.error("Failed to count chat members", err);
              return null;
            })
          )
        ),
        chatsApi.lastMessages(chats.map((c) => c.id)).catch((err): Record<string, LastMessage> => {
          console.error("Failed to load last messages", err);
          return {};
        }),
      ]);
      setGroups(groupsNow);
      setRows(
        chats
          .map((chat, i) => ({
            chat,
            ...kindOf(chat, groupsNow),
            members: counts[i],
            lastAt: last[chat.id]?.created_at ?? null,
          }))
          .sort((a, b) => byName(a.chat, b.chat))
      );
    } catch (err: any) {
      console.error("Failed to load chats", err);
      setError(err?.message || "Не удалось загрузить чаты");
    }
  }, []);

  const loadPeople = useCallback(async () => {
    try {
      const [players, coaches, parents] = await Promise.all([
        chatsApi.listPlayers(),
        chatsApi.listCoaches(),
        chatsApi.listParents(),
      ]);
      // Deactivated children and parents cannot log in, so they are not offered.
      setPeople([
        ...(players ?? [])
          .filter((p) => p.is_active)
          .sort(byName)
          .map((p): Person => ({ key: `player:${p.id}`, id: p.id, type: "player", name: p.name, groupID: p.group_id })),
        ...(coaches ?? [])
          .slice()
          .sort(byName)
          .map((c): Person => ({ key: `coach:${c.id}`, id: String(c.id), type: "coach", name: c.name, groupID: null })),
        ...(parents ?? [])
          .filter((p) => p.is_active)
          .sort(byName)
          .map((p): Person => ({ key: `parent:${p.id}`, id: p.id, type: "parent", name: p.name, groupID: null })),
      ]);
    } catch (err: any) {
      console.error("Failed to load people for chats", err);
      setError(err?.message || "Не удалось загрузить детей, тренеров и родителей");
    }
  }, []);

  const loadDetails = useCallback(async (chatID: string) => {
    try {
      const [memberList, banList] = await Promise.all([chatsApi.listMembers(chatID), chatsApi.listBans(chatID)]);
      if (openRef.current !== chatID) return;
      setMembers(memberList);
      setBans(banList ?? []);
    } catch (err: any) {
      console.error("Failed to load chat members", err);
      setError(err?.message || "Не удалось загрузить участников чата");
    }
  }, []);

  useEffect(() => {
    Promise.all([refreshChats(), loadPeople()]).finally(() => setLoading(false));
  }, [refreshChats, loadPeople]);

  useEffect(() => {
    if (openID) detailsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [openID]);

  const run = async (action: () => Promise<unknown>, failure: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    let ok = false;
    try {
      await action();
      ok = true;
    } catch (err: any) {
      console.error("Chat action failed", err);
      setError(err?.message || failure);
    } finally {
      setBusy(false);
    }
    await Promise.all([refreshChats(), openRef.current ? loadDetails(openRef.current) : null]);
    return ok;
  };

  const groupName = new Map(groups.map((g) => [g.id, g.name]));
  const me = currentAdmin();
  const newName = name.trim();

  const openChat = (row: ChatRow) => {
    setError(null);
    openRef.current = row.chat.id;
    setOpenID(row.chat.id);
    setMembers(null);
    setBans([]);
    setAddPicked([]);
    setBanUser("");
    setBanDuration("forever");
    setBanReason("");
    loadDetails(row.chat.id);
  };

  const closeChat = () => {
    openRef.current = null;
    setOpenID(null);
    setMembers(null);
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName) return;
    const admin = currentAdmin();
    if (!admin) {
      setError("Не получилось понять, кто вы. Выйдите и войдите заново.");
      return;
    }
    // With members in the body the server does not add the creator, and it
    // lists only chats the caller is in: without the admin the chat would
    // never show up here.
    const chatMembers: NewChatMember[] = [
      { user_id: admin.id, user_type: "admin", user_name: admin.name, role: "admin" },
      ...toMembers(people, picked),
    ];
    const created = await run(() => chatsApi.createChat(newName, chatMembers), "Не удалось создать чат");
    if (created) {
      setName("");
      setPicked([]);
    }
  };

  const removeChat = async (row: ChatRow) => {
    const confirmed = await confirm({
      title: `Удалить чат «${row.chat.name}»?`,
      body: (
        <>
          <p>
            Вместе с чатом удалятся вся переписка, участники, блокировки и отметки о прочтении. У всех
            участников чат пропадёт из приложения.
          </p>
          {row.kind === "team" && (
            <p>
              Группа «{row.group?.name}» останется без командного чата. Создать новый можно в разделе
              «Группы».
            </p>
          )}
          {row.kind === "parents" && (
            <p>
              {row.group ? `Группа «${row.group.name}»` : "Группа"} останется без родительского чата, а
              создать его заново из админки нельзя.
            </p>
          )}
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить чат",
      danger: true,
    });
    if (!confirmed) return;
    if (openRef.current === row.chat.id) closeChat();
    await run(() => chatsApi.deleteChat(row.chat.id), "Не удалось удалить чат");
  };

  const openRow = openID ? rows.find((r) => r.chat.id === openID) ?? null : null;
  const memberIDs = new Set((members ?? []).map((m) => m.user_id));
  // Only people not in the chat yet: the server upserts a member, so adding
  // someone again would reset their role to a plain member.
  const addable = people.filter((p) => !memberIDs.has(p.id));
  const bannedIDs = new Set(bans.map((b) => b.user_id));
  const bannable = (members ?? []).filter((m) => m.user_id !== me?.id && !bannedIDs.has(m.user_id));
  const memberName = new Map((members ?? []).map((m) => [m.user_id, m.user_name]));

  const addMembers = async (e: FormEvent) => {
    e.preventDefault();
    if (!openRow || addPicked.length === 0) return;
    const added = await run(
      () => chatsApi.addMembers(openRow.chat.id, toMembers(addable, addPicked)),
      "Не удалось добавить участников"
    );
    if (added) setAddPicked([]);
  };

  const removeMember = async (member: ChatMember) => {
    if (!openRow) return;
    const isMe = member.user_id === me?.id;
    const confirmed = await confirm({
      title: `Убрать «${member.user_name}» из чата?`,
      body: (
        <>
          <p>
            {isMe
              ? "Это вы. Чат пропадёт из этого списка, и вернуть себя в него отсюда уже не получится."
              : "Участник потеряет доступ к переписке. Сообщения, которые он уже написал, останутся в чате."}
          </p>
          {openRow.kind !== "custom" && <p>{syncNote(openRow)}</p>}
        </>
      ),
      confirmLabel: "Убрать",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => chatsApi.removeMember(openRow.chat.id, member.user_id), "Не удалось убрать участника");
  };

  const ban = async (e: FormEvent) => {
    e.preventDefault();
    if (!openRow || !banUser) return;
    const banned = await run(
      () => chatsApi.ban(openRow.chat.id, banUser, banDuration, banReason.trim() || null),
      "Не удалось заблокировать участника"
    );
    if (banned) {
      setBanUser("");
      setBanDuration("forever");
      setBanReason("");
    }
  };

  const unban = (b: ChatBan) =>
    openRow && run(() => chatsApi.unban(openRow.chat.id, b.user_id), "Не удалось снять блокировку");

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide chats-page">
      <h2>Чаты</h2>
      <p className="page-description">
        Здесь чаты, в которых вы состоите: командные и родительские чаты групп и отдельные чаты, которые
        вы создали. Чаты, где вас нет, сервер не показывает. Переименовать чат или сменить его цвет можно
        только в приложении.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form className="mav-card cp-form chats-form" aria-label="Новый чат" onSubmit={create}>
        <h3 className="chats-form-title">Новый чат</h3>
        <label className="cp-field chats-form-name">
          <span>Название</span>
          <input
            className="mav-input"
            value={name}
            maxLength={100}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <PeoplePicker
          legend="Кого добавить"
          people={people}
          picked={picked}
          groupName={groupName}
          emptyText="Детей, тренеров и родителей пока нет"
          disabled={busy}
          onChange={setPicked}
        />
        <p className="mav-hint chats-form-hint">
          Вы попадёте в чат сами. Командные и родительские чаты групп сервер собирает сам — отдельный чат
          нужен для всего остального.
        </p>
        <div className="cp-actions">
          <button type="submit" className="mav-btn-primary" disabled={busy || !newName}>
            Создать чат
          </button>
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table chats-table" aria-label="Чаты">
          <thead>
            <tr>
              <th>Чат</th>
              <th>Тип</th>
              <th>Участники</th>
              <th>Последнее сообщение</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  {error ? "—" : "Чатов пока нет"}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.chat.id} className={row.chat.id === openID ? "is-open" : undefined}>
                  <td className="cell-chat-name">{row.chat.name}</td>
                  <td className="cell-chat-kind">
                    <span className={row.kind === "custom" ? "mav-badge mav-badge-active" : "mav-badge mav-badge-muted"}>
                      {KIND_LABEL[row.kind]}
                    </span>
                  </td>
                  <td className="cell-chat-members" data-label="Участников:">
                    {row.members ?? "—"}
                  </td>
                  <td className="date-cell cell-chat-last">
                    {row.lastAt ? formatDateTime(row.lastAt) : <span className="mav-status-muted">Сообщений нет</span>}
                  </td>
                  <td className="cell-action">
                    <div className="row-actions">
                      <button className="mav-btn-secondary" disabled={busy} onClick={() => openChat(row)}>
                        Открыть
                      </button>
                      <button className="btn-quiet-danger" disabled={busy} onClick={() => removeChat(row)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {openRow && (
        <section ref={detailsRef} className="chats-details" aria-label={`Чат «${openRow.chat.name}»`}>
          <div className="chats-details-header">
            <h3>Чат «{openRow.chat.name}»</h3>
            <span className="mav-badge mav-badge-muted">{KIND_LABEL[openRow.kind]}</span>
            <button type="button" className="mav-btn-secondary chats-details-close" onClick={closeChat}>
              Закрыть
            </button>
          </div>

          {openRow.kind !== "custom" && <p className="chats-sync-note">{syncNote(openRow)}</p>}

          {members === null ? (
            <p className="mav-hint">Загружаем участников…</p>
          ) : (
            <>
              <h4>Участники: {members.length}</h4>
              <div className="table-wrap">
                <table className="settings-table stack-table chats-members-table" aria-label="Участники">
                  <thead>
                    <tr>
                      <th>Участник</th>
                      <th>Кто</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="empty-cell">
                          В чате никого нет
                        </td>
                      </tr>
                    ) : (
                      members.map((m) => (
                        <tr key={m.user_id}>
                          <td className="cell-member-name">
                            <span className="chats-member-name">{m.user_name || m.user_id}</span>
                            {m.user_id === me?.id && <span className="mav-badge mav-badge-muted">вы</span>}
                          </td>
                          <td className="cell-member-type">{TYPE_LABEL[m.user_type] ?? m.user_type}</td>
                          <td className="cell-action">
                            <div className="row-actions">
                              <button className="btn-quiet-danger" disabled={busy} onClick={() => removeMember(m)}>
                                Убрать
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <form className="chats-subform" aria-label="Добавить участников" onSubmit={addMembers}>
                <PeoplePicker
                  key={openRow.chat.id}
                  legend="Кого добавить"
                  people={addable}
                  picked={addPicked}
                  groupName={groupName}
                  emptyText="Добавлять некого — все уже в чате"
                  disabled={busy}
                  onChange={setAddPicked}
                />
                <div className="cp-actions">
                  <button type="submit" className="mav-btn-primary" disabled={busy || addPicked.length === 0}>
                    Добавить участников
                  </button>
                </div>
              </form>

              <h4>Блокировки</h4>
              <p className="mav-hint">Заблокированный остаётся в чате и видит переписку, но писать в него не может.</p>
              {bans.length === 0 ? (
                <p className="chats-empty">Заблокированных нет</p>
              ) : (
                <div className="table-wrap">
                  <table className="settings-table stack-table chats-bans-table" aria-label="Блокировки">
                    <thead>
                      <tr>
                        <th>Участник</th>
                        <th>Срок</th>
                        <th>Причина</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {bans.map((b) => (
                        <tr key={b.user_id}>
                          <td className="cell-ban-name">{memberName.get(b.user_id) ?? b.user_id}</td>
                          <td className="date-cell cell-ban-until">
                            {b.expires_at ? `до ${formatDateTime(b.expires_at)}` : "навсегда"}
                          </td>
                          <td className="cell-ban-reason">{b.reason || "—"}</td>
                          <td className="cell-action">
                            <div className="row-actions">
                              <button className="mav-btn-secondary" disabled={busy} onClick={() => unban(b)}>
                                Разблокировать
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <form className="chats-subform chats-ban-form" aria-label="Блокировка участника" onSubmit={ban}>
                <label className="cp-field">
                  <span>Участник</span>
                  <select className="mav-input" value={banUser} disabled={busy} onChange={(e) => setBanUser(e.target.value)}>
                    <option value="">Выберите участника</option>
                    {bannable.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.user_name || m.user_id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-field">
                  <span>Срок</span>
                  <select
                    className="mav-input"
                    value={banDuration}
                    disabled={busy}
                    onChange={(e) => setBanDuration(e.target.value as BanDuration)}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="cp-field chats-ban-reason">
                  <label className="cp-field">
                    <span>Причина</span>
                    <input
                      className="mav-input"
                      value={banReason}
                      maxLength={200}
                      disabled={busy}
                      onChange={(e) => setBanReason(e.target.value)}
                    />
                  </label>
                  <span className="mav-hint">Необязательно</span>
                </div>
                <div className="cp-actions">
                  <button type="submit" className="mav-btn-primary" disabled={busy || !banUser}>
                    Заблокировать
                  </button>
                </div>
              </form>
            </>
          )}
        </section>
      )}

      {dialog}
    </div>
  );
}
